import { Button, IconButton, RadioGroup, useMediaQuery } from '@mui/material';
import Image from 'next/image';
import React, { useMemo, useRef, useState } from 'react';
import {
  IMAGE_LIST_INVENTORY,
  PRODUCT_DETAILS_INVENTORY,
  PRODUCT_OFFERS,
} from '../../../../../commons/Constants/PDP';
import BaseCarousel from '../../../../../commons/components/ui/mui5/BaseCarousel/BaseCarousel';
import { productsKeyConstants } from '../../../../../config';
import { TextWithArrowAnimation } from '../../../../../commons/hoc';
import imgLoader from '../../../../../imgloader';
import ArrowIcon from '../../../../mui5/homepage/bannerCarousal/ArrowIcon';
import styles from './productGalleryInfo.module.scss';
import { ProductsCategory } from '../../../../../config';
import { useRouter } from 'next/router';
import { RatingWithCount } from '../../../../../commons/hoc';
import NewOfferCard from '../newOfferCard/newOfferCard';
import { useEffect } from 'react';
import VariantSelector from '../../VitaminDK/VariantSelector';
import { variantOptions } from '../../VitaminDK/variantData';
import { useAppDispatch } from '../../../../../src/app/hooks';
import { setSelectedVariantData } from '../../../../../src/features/user/userState';
function ProductGalleryInfo({
  productType = ProductsCategory.RELIEF_FACTOR,
  id = '',
  handleSubmit = () => {},
  variationName,
  isShowReview = false,
}) {
  const dispatch = useAppDispatch();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(
    ProductsCategory.VITAMIN_D_K2
  );
  const router = useRouter();
  const variantFromQuery = router.query.variant;
  useEffect(() => {
    if (
      [ProductsCategory.VITAMIN_D_K2, ProductsCategory.VITAMIN_D_K5].includes(
        productType
      )
    ) {
      const normalized = (variantFromQuery || 'D_K2')?.replace('D_', 'D'); // DK2 or DK5
      setSelectedVariant(normalized);
      dispatch(setSelectedVariantData(variantFromQuery || 'D_K2'));
    }
  }, [variantFromQuery, productType]);
  const productDetails = useMemo(() => {
    if (
      [ProductsCategory.VITAMIN_D_K2, ProductsCategory.VITAMIN_D_K5].includes(
        productType
      )
    ) {
      return PRODUCT_DETAILS_INVENTORY[selectedVariant];
    }
    return PRODUCT_DETAILS_INVENTORY[productType];
  }, [productType, selectedVariant]);

  const imageList = useMemo(() => {
    if (
      [ProductsCategory.VITAMIN_D_K2, ProductsCategory.VITAMIN_D_K5].includes(
        productType
      )
    ) {
      return IMAGE_LIST_INVENTORY[selectedVariant];
    }
    return IMAGE_LIST_INVENTORY[productType];
  }, [productType, selectedVariant]);

  const offers = useMemo(() => {
    if (
      [ProductsCategory.VITAMIN_D_K2, ProductsCategory.VITAMIN_D_K5].includes(
        productType
      )
    ) {
      return PRODUCT_OFFERS[selectedVariant];
    } else {
      return PRODUCT_OFFERS[productType];
    }
  }, [productType, selectedVariant]);
  const baseCarouselRef = useRef();
  const galleryRef = useRef();
  const isTablet = useMediaQuery('(max-width:992px)');
  const isMobile = useMediaQuery('(max-width:576px)');
  const slides = useMemo(
    () =>
      imageList.map((item, index) => {
        return {
          key: index,
          component: (
            <Image
              style={{
                backgroundColor: 'white',
              }}
              src={item.img}
              alt={item.type}
              loading="eager"
              loader={imgLoader}
              className={styles.productGalleryImg}
              placeholder="blur"
              blurDataURL={item.img}
            />
          ),
        };
      }),
    [imageList]
  );

  const handleSlideChange = (index) => setCurrentSlide(index);

  const next = () => {
    if (baseCarouselRef.current) baseCarouselRef.current.handleNext();
  };
  const prev = () => {
    if (baseCarouselRef.current) baseCarouselRef.current.handlePrev();
  };
  const onSubmit = (selectedOffer) => {
    handleSubmit(selectedOffer);
  };
  const handleSupplementClick = () => {
    if (galleryRef.current) {
      galleryRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    const slideIndex = imageList.findIndex((item) => item.type == 'supplement');
    changeSlide(slideIndex);
  };
  const changeSlide = (idx) => {
    if (baseCarouselRef.current) {
      baseCarouselRef.current.updateCurrentSlide(idx);
    }
  };

  return (
    <div className={styles.productGalleryExtender} id={id}>
      <div className={styles.productGalleryInfo}>
        <div className={styles.parentContainer}>
          {isMobile && (
            <div className={styles.rightColumn}>
              {[
                ProductsCategory.VITAMIN_D_K2,
                ProductsCategory.VITAMIN_D_K5,
              ].includes(productType) && (
                <VariantSelector
                  variants={variantOptions}
                  selectedVariant={selectedVariant}
                  onSelect={(newVariant) => {
                    setSelectedVariant(newVariant);
                    const modifiedVariant = newVariant.replace('D', 'D_');
                    dispatch(setSelectedVariantData(modifiedVariant));
                    router.replace(
                      {
                        pathname: router.pathname,
                        query: {
                          ...router.query,
                          variant: modifiedVariant,
                        },
                      },
                      router.asPath,
                      { shallow: true }
                    );
                  }}
                />
              )}
              <ProductHeaderInfo
                avgRating={productDetails.totalRatings}
                prodCategory={productDetails.prodCategory}
                reviewsCount={productDetails.reviewsCount}
                productType={
                  [
                    ProductsCategory.VITAMIN_D_K2,
                    ProductsCategory.VITAMIN_D_K5,
                  ].includes(productType)
                    ? selectedVariant
                    : productType
                }
                isShowReview={isShowReview}
              />
              <h1 className={styles.title}>{productDetails.prodTitle}</h1>
            </div>
          )}
          <div className={styles.leftColumn}>
            <div className={styles.innerContainer} ref={galleryRef}>
              <div
                style={{ width: isMobile ? '100%' : isTablet ? '90%' : '95%' }}
              >
                <BaseCarousel
                  ref={baseCarouselRef}
                  items={slides}
                  options={getConfigurableProps()}
                  handleSlideChange={handleSlideChange}
                ></BaseCarousel>
              </div>

              {!isTablet && (
                <>
                  <div
                    style={{ position: 'absolute', bottom: '5%', left: '5%' }}
                  >
                    <ThumbnailGallery
                      currentSlide={currentSlide}
                      handleSlideChange={changeSlide}
                      thumbnailList={imageList}
                    />
                  </div>
                  <div
                    style={{ position: 'absolute', bottom: '5%', right: '5%' }}
                  >
                    <NavArrows
                      handleNext={next}
                      handlePrev={prev}
                      nextDisabled={currentSlide == imageList.length - 1}
                      prevDisabled={currentSlide == 0}
                    ></NavArrows>
                  </div>
                </>
              )}
            </div>
            {isTablet && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: '1rem',
                  marginBottom: '1rem',
                }}
              >
                <ThumbnailGallery
                  currentSlide={currentSlide}
                  handleSlideChange={changeSlide}
                  thumbnailList={imageList}
                />
              </div>
            )}
          </div>
          <div className={styles.rightColumn}>
            {!isMobile && (
              <>
                {[
                  ProductsCategory.VITAMIN_D_K2,
                  ProductsCategory.VITAMIN_D_K5,
                ].includes(productType) && (
                  <VariantSelector
                    variants={variantOptions}
                    selectedVariant={selectedVariant}
                    onSelect={(newVariant) => {
                      setSelectedVariant(newVariant);
                      const modifiedVariant = newVariant.replace('D', 'D_');
                      dispatch(setSelectedVariantData(modifiedVariant));
                      router.replace(
                        {
                          pathname: router.pathname,
                          query: {
                            ...router.query,
                            variant: modifiedVariant,
                          },
                        },
                        router.asPath,
                        { shallow: true }
                      );
                    }}
                  />
                )}
                <ProductHeaderInfo
                  avgRating={productDetails.totalRatings}
                  prodCategory={productDetails.prodCategory}
                  reviewsCount={productDetails.reviewsCount}
                  productType={
                    [
                      ProductsCategory.VITAMIN_D_K2,
                      ProductsCategory.VITAMIN_D_K5,
                    ].includes(productType)
                      ? selectedVariant
                      : productType
                  }
                  isShowReview={isShowReview}
                />
                <h1 className={styles.title}>{productDetails.prodTitle}</h1>
              </>
            )}
            <span
              className={styles.subTitle}
              dangerouslySetInnerHTML={{ __html: productDetails.subTitle }}
            ></span>
            {productDetails?.prodDesc ? (
              <div className={styles.prodDescription}>
                {productDetails.prodDesc}
                {/* <a className={styles.copyRightSymbol}>
                  {productDetails?.prodCategory === ''
                    ? ''
                    : productDetails?.prodCategory === 'Daily Essentials'
                      ? '*'
                      : '**'}
                </a> */}
              </div>
            ) : (
              <></>
            )}
            <OfferPricingForm
              offerList={offers}
              handleSubmit={onSubmit}
              variationName={variationName}
            ></OfferPricingForm>
            <div className={styles.direction}>
              <span className={styles.title}>Directions</span>
              <p className={styles.description}>
                {productDetails.directionDesc}
              </p>
            </div>
            <SupplementLabelBtn handleOnClick={handleSupplementClick}>
              <span className={styles.supplementLabel}>
                View Supplement Label
              </span>
            </SupplementLabelBtn>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductGalleryInfo;

const getConfigurableProps = () => ({
  showArrows: false,
  showStatus: false,
  showIndicators: false,
  infiniteLoop: true,
  showThumbs: false,
  useKeyboardArrows: true,
  stopOnHover: true,
  swipeable: true,
  dynamicHeight: true,
  emulateTouch: true,
  autoFocus: false,
  thumbWidth: 100,
  // selectedItem: 0,
  interval: 2000,
  transitionTime: 500,
  swipeScrollTolerance: 5,
  preventMovementUntilSwipeScrollTolerance: true,
});

const SupplementLabelBtn = ({ children, handleOnClick }) => {
  return (
    <Button
      className={styles.supplementLabelBtn}
      onClick={handleOnClick}
      sx={{
        'width': 'fit-content',
        'padding': '0px',
        ':hover': {
          backgroundColor: 'transparent',
        },
      }}
    >
      <TextWithArrowAnimation textTransform={'none'} underline type={'dark'}>
        {children}
      </TextWithArrowAnimation>
    </Button>
  );
};

const NavArrows = ({ handlePrev, handleNext, nextDisabled, prevDisabled }) => {
  return (
    <div className={styles.nav}>
      <IconButton
        className={styles.arrowsIcon}
        color="primary"
        size="large"
        onClick={handlePrev}
        disabled={prevDisabled}
      >
        <ArrowIcon isRotated={true} color={'black'} />
      </IconButton>
      <IconButton
        className={styles.arrowsIcon}
        color="primary"
        size="large"
        onClick={handleNext}
        disabled={nextDisabled}
      >
        <ArrowIcon isRotated={false} color={'black'} />
      </IconButton>
    </div>
  );
};

const ThumbnailGallery = ({
  thumbnailList,
  currentSlide,
  handleSlideChange,
}) => {
  return (
    <div className={styles['thumbs-container']}>
      {thumbnailList.map((thumb, index) => (
        <div
          onClick={() => handleSlideChange(index)}
          className={`${styles['circle-container']} ${
            currentSlide == index ? styles['selectedItem'] : ''
          } `}
          key={thumb.id}
        >
          <div className={styles['inner-circle']}>
            <Image
              alt={thumb.type}
              src={thumb.img}
              className={styles['circle-image']}
              objectFit="cover"
              objectPosition="center"
              loader={imgLoader}
              placeholder="blur"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const OfferPricingForm = ({
  handleSubmit = () => {},
  offerList,
  variationName,
}) => {
  const [value, setValue] = useState(
    offerList?.length > 0 ? offerList[0].radioButtonValue : ''
  );

  const onSubmit = (event, value) => {
    event.preventDefault();
    handleSubmit(value);
  };
  return (
    <form className={styles.offerForm} onSubmit={(e) => onSubmit(e, value)}>
      <RadioGroup
        className={styles.priceCardContaier}
        name="offerPrice"
        value={value}
        onChange={(event) => setValue(event.target.value)}
      >
        <NewOfferCard
          offerList={offerList}
          radiovalue={value}
          childtoParent={onSubmit}
          variationName={variationName}
        />
      </RadioGroup>
    </form>
  );
};

const ProductHeaderInfo = ({
  prodCategory,
  productType,
  isShowReview = false,
}) => {
  const router = useRouter();
  const handleRatingOnClick = () => {
    router.push('#review', undefined, {
      shallow: true,
      scroll: false,
    });
  };

  const reviewProduct = {
    REL: productsKeyConstants.RF_RELIEF_FACTOR_QS,
    GOF: productsKeyConstants.RF_GO_FACTOR,
    ZEN: productsKeyConstants.RF_ZEN_FACTOR,
    ZFA: productsKeyConstants.RF_Z_FACTOR,
  };
  return (
    isShowReview && (
      <div className={styles.contentFirstLine}>
        <h6 className={styles.prodCategory}>{prodCategory}</h6>

        <div className={styles.ratingContainer} onClick={handleRatingOnClick}>
          {isShowReview && (
            <RatingWithCount
              displayScore={false}
              fetchFromYotpo={true}
              size="small"
              className={styles.ratingStyle}
              product={reviewProduct[productType]}
            />
          )}
        </div>
      </div>
    )
  );
};
